import React, { useEffect, useMemo, useState } from "react";
import api, { extractApiError, unwrapApiResponse } from "../api/api";
import { useToast } from "./ToastProvider";

export default function TenantCleanupView({ getToken, metadata }) {
  const { showError, showSuccess } = useToast();
  const tenantOptions = metadata?.tenantOptions || [];
  const datasetOptions = metadata?.datasetOptions || [];
  const defaultTenantId = metadata?.defaultTenantId || tenantOptions[0]?.value || "";
  const defaultDataset = metadata?.defaultDataset || datasetOptions[0]?.key || "";

  const [tenantId, setTenantId] = useState(defaultTenantId);
  const [dataset, setDataset] = useState(defaultDataset);
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState({ loading: false, error: "" });
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!tenantOptions.length) {
      setTenantId("");
      return;
    }

    if (!tenantOptions.some((option) => option.value === tenantId)) {
      setTenantId(defaultTenantId);
    }
  }, [defaultTenantId, tenantId, tenantOptions]);

  useEffect(() => {
    if (!datasetOptions.length) {
      setDataset("");
      return;
    }

    if (!datasetOptions.some((option) => option.key === dataset)) {
      setDataset(defaultDataset);
    }
  }, [dataset, datasetOptions, defaultDataset]);

  const activeDataset = useMemo(
    () => datasetOptions.find((option) => option.key === dataset) || null,
    [dataset, datasetOptions]
  );

  async function handleCleanup() {
    if (!tenantId || !dataset) {
      const message = "Selecciona una entidad y un dataset antes de continuar.";
      setStatus({ loading: false, error: message });
      showError(message);
      return;
    }

    if (!confirmed) {
      const message = "Confirma la limpieza antes de ejecutar el borrado.";
      setStatus({ loading: false, error: message });
      showError(message);
      return;
    }

    try {
      setStatus({ loading: true, error: "" });
      const token = await getToken({ skipCache: true });
      const response = await api.post("/query/cleanup", {
        tenantId,
        dataset
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const cleanupResult = unwrapApiResponse(response);
      setResult(cleanupResult);
      setStatus({ loading: false, error: "" });
      setConfirmed(false);
      showSuccess(`Se lanzó la limpieza para ${cleanupResult?.tenantId || tenantId} en ${cleanupResult?.datasetLabel || dataset}.`);
    } catch (error) {
      const message = extractApiError(error, "No se pudo ejecutar la limpieza");
      setStatus({ loading: false, error: message });
      showError(message);
    }
  }

  return (
    <section className="monitor-layout">
      <section className="table-card cleanup-card">
        <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
          <div>
            <div className="brand-badge">Limpieza</div>
            <h2 className="section-title with-margin">Eliminar información por tenant</h2>
            <p className="section-subtitle">Borra todos los registros del tenant seleccionado en las tablas del dataset.</p>
          </div>
        </div>

        {!tenantOptions.length ? (
          <p className="error-text">No hay entidades disponibles para ejecutar la limpieza.</p>
        ) : (
          <div className="cleanup-layout">
            <div className="builder-card cleanup-form-card">
              <div className="builder-grid cleanup-grid">
                <label className="field">
                  <span>Entidad</span>
                  <select value={tenantId} onChange={(event) => setTenantId(event.target.value)} disabled={status.loading}>
                    {tenantOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Dataset</span>
                  <select value={dataset} onChange={(event) => setDataset(event.target.value)} disabled={status.loading}>
                    {datasetOptions.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="cleanup-warning-box">
                <strong>Acción destructiva</strong>
                <p>Esta operación elimina registros en tablas base y resumen para el tenant seleccionado.</p>
                {activeDataset ? (
                  <p className="section-subtitle">Tablas objetivo: {activeDataset.tables.join(", ")}</p>
                ) : null}
              </div>

              <label className="cleanup-confirm-row">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                  disabled={status.loading}
                />
                <span>Confirmo que deseo eliminar la información del tenant seleccionado.</span>
              </label>

              <div className="user-admin-create-actions cleanup-actions">
                <button className="primary-button" type="button" onClick={handleCleanup} disabled={status.loading || !tenantId || !dataset}>
                  {status.loading ? "Eliminando..." : "Eliminar registros"}
                </button>
              </div>

              {status.error ? <p className="error-text">{status.error}</p> : null}
            </div>

            <section className="table-card cleanup-result-card">
              <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
                <div>
                  <h3 className="section-title">Resultado</h3>
                  <p className="section-subtitle">Resumen de tablas afectadas por la limpieza.</p>
                </div>
              </div>

              {!result ? (
                <p className="section-subtitle">Todavía no se ha ejecutado ninguna limpieza.</p>
              ) : (
                <div className="cleanup-result-body">
                  <div className="monitor-stats-grid cleanup-stats-grid">
                    <article className="table-card stat-card">
                      <span className="section-subtitle">Entidad</span>
                      <strong className="stat-value cleanup-stat-value">{result.tenantId}</strong>
                    </article>
                    <article className="table-card stat-card">
                      <span className="section-subtitle">Dataset</span>
                      <strong className="stat-value cleanup-stat-value">{result.datasetLabel}</strong>
                    </article>
                    <article className="table-card stat-card">
                      <span className="section-subtitle">Tablas afectadas</span>
                      <strong className="stat-value cleanup-stat-value">{result.tablesAffected?.length || 0}</strong>
                    </article>
                  </div>

                  <div className="cleanup-query-block">
                    <strong>Tablas afectadas</strong>
                    <p className="section-subtitle">{(result.tablesAffected || []).join(", ") || "Ninguna"}</p>
                    {result.missingTables?.length ? (
                      <p className="section-subtitle">Tablas no encontradas: {result.missingTables.join(", ")}</p>
                    ) : null}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </section>
    </section>
  );
}
